import { useState, useCallback, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@/lib/api.js";
import { Skeleton } from "@bongeunsa/ui";
import { ConfirmDialog } from "@/components/ConfirmDialog.js";
import { I18nEditor, EMPTY_I18N } from "@/components/I18nEditor.js";
import { DefaultLanguagePicker } from "@/components/DefaultLanguagePicker.js";
import { SlidePanel } from "@/components/SlidePanel.js";
import { InlineCompletion } from "@/components/CompletionBadge.js";
import { useToast } from "@/components/Toast.js";
import { computeI18nCompletion } from "@/lib/i18nCompletion.js";
import type { Survey, SurveyQuestion, SurveyChoice } from "@bongeunsa/db";
import type { Language } from "@bongeunsa/i18n";

const PUBLIC_BASE = import.meta.env.DEV ? "http://localhost:5173" : "https://bongeunsa.app";

type SurveyFull = {
  survey: Survey;
  questions: Array<SurveyQuestion & { choices: SurveyChoice[] }>;
};

export function SurveyEditPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { show: showToast, ToastUI } = useToast();

  const sensors = useSensors(useSensor(PointerSensor));
  const [justDroppedId, setJustDroppedId] = useState<string | null>(null);
  const dropTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { data, isLoading } = useQuery({
    queryKey: ["survey-edit", id],
    queryFn: () => api.get<SurveyFull>(`/api/surveys/${id}`),
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["survey-edit", id] });
  }

  const reorderQuestionsMutation = useMutation({
    mutationFn: (orderedIds: string[]) =>
      api.put(`/api/surveys/${id}/questions/reorder`, { ordered_ids: orderedIds }),
    onSuccess: invalidate,
    onError: () => showToast("순서 변경에 실패했습니다.", "error"),
  });

  function handleQuestionsDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id || !data) return;
    const qs = data.questions;
    const oldIndex = qs.findIndex((q) => q.id === active.id);
    const newIndex = qs.findIndex((q) => q.id === over.id);
    const reordered = arrayMove(qs, oldIndex, newIndex);
    qc.setQueryData(["survey-edit", id], (old: typeof data) =>
      old ? { ...old, questions: reordered } : old
    );
    clearTimeout(dropTimerRef.current);
    setJustDroppedId(active.id as string);
    dropTimerRef.current = setTimeout(() => setJustDroppedId(null), 400);
    reorderQuestionsMutation.mutate(reordered.map((q) => q.id));
  }
  function onSaved(msg = "저장되었습니다.") { invalidate(); showToast(msg); }
  function onDeleted(msg = "삭제되었습니다.") { invalidate(); showToast(msg); }
  function onSaveError() { showToast("오류가 발생했습니다.", "error"); }

  if (isLoading) {
    return (
      <div className="p-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full rounded-xl" />
      </div>
    );
  }

  if (!data) return null;
  const { survey, questions } = data;

  return (
    <div className="flex-1 overflow-y-auto p-8">
      {ToastUI}
      <div className="max-w-2xl">
        <div className="mb-6 flex items-center gap-3">
          <Link to="/surveys" className="text-sm text-gray-400 hover:text-gray-600">
            ← 목록
          </Link>
          <span className="text-gray-300">/</span>
          <h1 className="text-xl font-bold text-gray-900">{survey.name}</h1>
          {survey.is_active ? (
            <span className="rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700">
              액티브
            </span>
          ) : null}
        </div>

        {/* 제목(name_i18n) + default language */}
        <NameI18nEditor surveyId={id!} survey={survey} onSuccess={invalidate} />

        {/* Slug editor */}
        <div className="mb-6 rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/5">
          <SlugEditor surveyId={id!} currentSlug={survey.slug} onSuccess={invalidate} />
          <p className="mt-2 text-xs text-gray-400">
            공개 URL: <span className="font-mono text-gray-600">{PUBLIC_BASE}/s/{survey.slug}</span>
          </p>
        </div>

        {/* Questions */}
        <section className="mb-8">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-800">문항 ({questions.length})</h2>
            <AddQuestionButton surveyId={id!} orderIndex={questions.length} onSuccess={() => onSaved("문항이 추가되었습니다.")} />
          </div>
          {questions.length === 0 ? (
            <EmptyState text="문항이 없습니다." />
          ) : (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleQuestionsDragEnd}>
              <SortableContext items={questions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-4">
                  {questions.map((q, qi) => (
                    <SortableQuestionCard
                      key={q.id}
                      question={q}
                      index={qi}
                      surveyId={id!}
                      onSaved={() => onSaved()} onDeleted={() => onDeleted("문항이 삭제되었습니다.")} onError={onSaveError}
                      isHighlighted={justDroppedId === q.id}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </section>
      </div>
    </div>
  );
}

// ── Slug Editor ───────────────────────────────────────────────

function SlugEditor({
  surveyId,
  currentSlug,
  onSuccess,
}: {
  surveyId: string;
  currentSlug: string;
  onSuccess: () => void;
}) {
  const [draft, setDraft] = useState(currentSlug);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => { setDraft(currentSlug); }, [currentSlug]);

  const check = useCallback(
    async (slug: string) => {
      if (slug === currentSlug || !slug) { setAvailable(null); return; }
      setChecking(true);
      try {
        const res = await api.get<{ available: boolean }>(`/api/surveys/check-slug/${slug}`);
        setAvailable(res.available);
      } finally {
        setChecking(false);
      }
    },
    [currentSlug]
  );

  useEffect(() => {
    const t = setTimeout(() => { if (draft !== currentSlug) check(draft); }, 500);
    return () => clearTimeout(t);
  }, [draft, currentSlug, check]);

  const mutation = useMutation({
    mutationFn: () => api.put(`/api/surveys/${surveyId}`, { slug: draft }),
    onSuccess: () => { setAvailable(null); onSuccess(); },
  });

  const slugChanged = draft !== currentSlug;
  const canSave = slugChanged && (available === true || available === null) && !checking;

  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-500">URL 슬러그</label>
      <div className="flex items-center gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
          className="flex-1 rounded-lg border border-gray-200 px-3 py-2 text-sm font-mono outline-none focus:border-brand-400"
        />
        {checking && <span className="text-xs text-gray-400">확인 중...</span>}
        {!checking && available === true && <span className="text-xs text-green-600">✓ 사용 가능</span>}
        {!checking && available === false && <span className="text-xs text-red-500">✗ 이미 사용 중</span>}
        <button
          onClick={() => mutation.mutate()}
          disabled={!canSave || mutation.isPending}
          className="rounded-lg bg-brand-500 px-3 py-2 text-xs font-semibold text-white disabled:opacity-40"
        >
          저장
        </button>
      </div>
    </div>
  );
}

// ── Question Card ─────────────────────────────────────────────

function SortableQuestionCard(props: React.ComponentProps<typeof QuestionCard>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.question.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style}>
      <QuestionCard {...props} isDragging={isDragging} dragHandle={
        <button {...attributes} {...listeners} className="mt-0.5 cursor-grab touch-none text-gray-300 hover:text-gray-500 active:cursor-grabbing shrink-0" aria-label="순서 변경">⠿</button>
      } />
    </div>
  );
}

function QuestionCard({
  question,
  index,
  surveyId,
  onSaved,
  onDeleted,
  onError,
  dragHandle,
  isDragging,
  isHighlighted,
}: {
  question: SurveyQuestion & { choices: SurveyChoice[] };
  index: number;
  surveyId: string;
  onSaved: () => void;
  onDeleted: () => void;
  onError: () => void;
  dragHandle?: React.ReactNode;
  isDragging?: boolean;
  isHighlighted?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [draftI18n, setDraftI18n] = useState(EMPTY_I18N);
  const [addingChoice, setAddingChoice] = useState(false);

  const koText = (() => {
    try {
      const obj = JSON.parse(question.i18n) as Record<Language, Record<string, string>>;
      return obj.ko ?? {};
    } catch { return {}; }
  })();

  const updateMutation = useMutation({
    mutationFn: () =>
      api.put(`/api/surveys/${surveyId}/questions/${question.id}`, { i18n: draftI18n }),
    onSuccess: () => { setEditing(false); onSaved(); },
    onError,
  });

  const deleteMutation = useMutation({
    mutationFn: () =>
      api.delete(`/api/surveys/${surveyId}/questions/${question.id}`),
    onSuccess: () => { setDeleteOpen(false); onDeleted(); },
    onError,
  });

  function openEdit() {
    try { setDraftI18n(JSON.parse(question.i18n) as typeof EMPTY_I18N); }
    catch { setDraftI18n(EMPTY_I18N); }
    setEditing(true);
  }

  return (
    <div className={`rounded-xl bg-white shadow-sm transition-shadow duration-300 ${isDragging ? "ring-2 ring-brand-300 shadow-lg" : isHighlighted ? "ring-2 ring-brand-400" : "ring-1 ring-black/5"}`}>
      <div className="flex items-start gap-3 p-4">
        {dragHandle}
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">
          {index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 line-clamp-2">
            {koText.question ?? "(질문 없음)"}
          </p>
          {koText.description && (
            <p className="mt-0.5 text-xs text-gray-400 line-clamp-1">{koText.description}</p>
          )}
          <I18nCompletionBadge i18nStr={question.i18n} fields={["question", "description"]} />
        </div>
        <div className="flex gap-1">
          <button onClick={openEdit} className="rounded px-2 py-1 text-xs text-gray-500 hover:bg-gray-50">
            편집
          </button>
          <button onClick={() => setDeleteOpen(true)} className="rounded px-2 py-1 text-xs text-red-400 hover:bg-red-50">
            삭제
          </button>
        </div>
      </div>

      {/* Choices */}
      <div className="border-t border-gray-50 px-4 pb-3">
        <div className="mt-2 space-y-1.5">
          {question.choices.map((ch, ci) => {
            const ckoText = (() => {
              try {
                const obj = JSON.parse(ch.i18n) as Record<Language, Record<string, string>>;
                return obj.ko ?? {};
              } catch { return {}; }
            })();
            return (
              <ChoiceRow
                key={ch.id}
                label={String.fromCharCode(65 + ci)}
                text={ckoText.label ?? "(선택지 없음)"}
                surveyId={surveyId}
                questionId={question.id}
                choiceId={ch.id}
                onSuccess={onSaved}
              />
            );
          })}
        </div>

        {addingChoice ? (
          <AddChoiceForm
            surveyId={surveyId}
            questionId={question.id}
            orderIndex={question.choices.length}
            onSuccess={() => { setAddingChoice(false); onSaved(); }}
            onCancel={() => setAddingChoice(false)}
          />
        ) : (
          <button onClick={() => setAddingChoice(true)} className="mt-2 text-xs text-brand-500 hover:underline">
            + 선택지 추가
          </button>
        )}
      </div>

      {/* Edit panel */}
      {editing && (
        <div className="border-t border-gray-100 p-4 space-y-3">
          <I18nEditor
            value={draftI18n}
            fields={[
              { key: "question", label: "질문" },
              { key: "description", label: "설명", multiline: true },
            ]}
            onChange={setDraftI18n}
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setEditing(false)} className="text-xs text-gray-500 hover:underline">
              취소
            </button>
            <button
              onClick={() => updateMutation.mutate()}
              disabled={updateMutation.isPending}
              className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
            >
              {updateMutation.isPending ? "저장 중..." : "저장"}
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteOpen}
        title="문항 삭제"
        description="이 문항과 모든 선택지가 삭제됩니다."
        confirmLabel="삭제"
        danger
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setDeleteOpen(false)}
      />
    </div>
  );
}

// ── Choice Row ────────────────────────────────────────────────

function ChoiceRow({
  label,
  text,
  surveyId,
  questionId,
  choiceId,
  onSuccess,
}: {
  label: string;
  text: string;
  surveyId: string;
  questionId: string;
  choiceId: string;
  onSuccess: () => void;
}) {
  const deleteMutation = useMutation({
    mutationFn: () =>
      api.delete(`/api/surveys/${surveyId}/questions/${questionId}/choices/${choiceId}`),
    onSuccess,
  });

  return (
    <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
      <span className="font-semibold text-gray-400">{label}</span>
      <span className="flex-1 truncate">{text}</span>
      <button
        onClick={() => deleteMutation.mutate()}
        className="text-red-300 hover:text-red-500"
        aria-label="선택지 삭제"
      >
        ✕
      </button>
    </div>
  );
}

// ── Add Choice Form ───────────────────────────────────────────

function AddChoiceForm({
  surveyId,
  questionId,
  orderIndex,
  onSuccess,
  onCancel,
}: {
  surveyId: string;
  questionId: string;
  orderIndex: number;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [draftI18n, setDraftI18n] = useState(EMPTY_I18N);

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/api/surveys/${surveyId}/questions/${questionId}/choices`, {
        order_index: orderIndex,
        i18n: draftI18n,
      }),
    onSuccess,
  });

  return (
    <div className="mt-2 rounded-xl border border-brand-200 bg-brand-50/30 p-3 space-y-2">
      <I18nEditor
        value={draftI18n}
        fields={[{ key: "label", label: "선택지 텍스트" }]}
        onChange={setDraftI18n}
      />
      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="text-xs text-gray-400 hover:underline">취소</button>
        <button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
        >
          추가
        </button>
      </div>
    </div>
  );
}

// ── Add Question Button ───────────────────────────────────────

function AddQuestionButton({
  surveyId,
  orderIndex,
  onSuccess,
}: {
  surveyId: string;
  orderIndex: number;
  onSuccess: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [draftI18n, setDraftI18n] = useState(EMPTY_I18N);

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/api/surveys/${surveyId}/questions`, {
        order_index: orderIndex,
        i18n: draftI18n,
      }),
    onSuccess: () => { setOpen(false); setDraftI18n(EMPTY_I18N); onSuccess(); },
  });

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="rounded-xl bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600"
      >
        + 문항 추가
      </button>
      {open && (
        <SlidePanel
          title="문항 추가"
          onClose={() => setOpen(false)}
          onSubmit={() => mutation.mutate()}
          loading={mutation.isPending}
        >
          <I18nEditor
            value={draftI18n}
            fields={[
              { key: "question", label: "질문" },
              { key: "description", label: "설명", multiline: true },
            ]}
            onChange={setDraftI18n}
          />
        </SlidePanel>
      )}
    </>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-xl bg-white p-8 text-center shadow-sm ring-1 ring-black/5">
      <p className="text-sm text-gray-400">{text}</p>
    </div>
  );
}

function I18nCompletionBadge({ i18nStr, fields }: { i18nStr: string; fields: string[] }) {
  const { overall } = computeI18nCompletion(i18nStr, fields);
  return <InlineCompletion pct={overall} />;
}

function NameI18nEditor({ surveyId, survey, onSuccess }: { surveyId: string; survey: Survey; onSuccess: () => void }) {
  const [nameI18n, setNameI18n] = useState<Record<Language, Record<string, string>>>(() => {
    try { return JSON.parse(survey.name_i18n) as Record<Language, Record<string, string>>; } catch { return EMPTY_I18N; }
  });
  const [defaultLang, setDefaultLang] = useState<Language>(survey.default_language as Language ?? "ko");
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  const isDirty = JSON.stringify(nameI18n) !== survey.name_i18n || defaultLang !== (survey.default_language ?? "ko");

  const mutation = useMutation({
    mutationFn: () => {
      const title = (nameI18n[defaultLang]?.title ?? "").trim();
      if (!title) { setError(`기본 언어(${defaultLang})의 제목을 입력하세요.`); return Promise.reject(); }
      setError("");
      return api.put(`/api/surveys/${surveyId}`, { name_i18n: JSON.stringify(nameI18n), default_language: defaultLang });
    },
    onSuccess: () => { setOpen(false); onSuccess(); },
  });

  return (
    <div className="mb-4 rounded-xl bg-white p-4 shadow-sm ring-1 ring-black/5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-gray-500">설문 제목 (다국어)</p>
          <p className="mt-0.5 text-sm font-medium text-gray-900 truncate">{(nameI18n["ko"]?.title ?? survey.name) || survey.name}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isDirty && (
            <button onClick={() => mutation.mutate()} disabled={mutation.isPending} type="button"
              className="rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50">
              {mutation.isPending ? "저장 중..." : "저장"}
            </button>
          )}
          <button onClick={() => setOpen(!open)} type="button"
            className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors">
            <span>{open ? "접기" : "다국어 편집"}</span>
            <svg className={`h-3 w-3 transition-transform duration-200 ${open ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-4 space-y-3">
          <DefaultLanguagePicker value={defaultLang} onChange={setDefaultLang} />
          <I18nEditor value={nameI18n} fields={[{ key: "title", label: "제목" }]} onChange={setNameI18n} defaultLang={defaultLang} requiredFields={["title"]} />
          {error && <p className="text-xs text-red-500">{error}</p>}
          <div className="flex justify-end">
            <button onClick={() => setOpen(false)} type="button" className="text-xs text-gray-500 hover:underline">닫기</button>
          </div>
        </div>
      )}
    </div>
  );
}
